import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import CustomDrawerModal from '../common/CustomDrawerModal'
import ActionButton from '../common/ActionButton'
import { PlusCircle } from 'lucide-react'
import { DialogFooter } from '../ui/dialog'
import { CommonModalType } from '@/utils/interfaces'
import { dispatch, useSelector } from '@/lib/store'
import { useEffect, useState } from 'react'
import { INSERT_ACTION } from '@/utils/constants'
import { askUserToAddPatient } from '@/lib/reducers/patientReducer'
import useCustomToast from '@/hooks/useCustomToast'
import {
  calculateAge,
  getAllPrimaryProfiles,
  isEmpty,
} from '@/utils/commonFunc'
import CART_HELPER from '@/helper/cartHelper'
import { updateCartData } from '@/lib/reducers/cartReducer'
import { updateLoginData } from '@/lib/reducers/loginReducer'
import PACKAGE_HELPER from '@/helper/packageHelper'
import PATIENT_HELPER from '@/helper/patientHelper'
import { initSavingDto, updateOrderData } from '@/lib/reducers/orderReducer'
import { updatePackageData } from '@/lib/reducers/packageReducer'
import { loadState } from '@/utils/localStorage'
import { useMediaQuery } from '@/hooks/use-media-query'

interface SelectMemberCommonModalType extends CommonModalType {
  isItPrimaryModal?: boolean
}

const SelectMemberModal = ({
  open,
  onOpenChange,
  isItPrimaryModal = false,
}: SelectMemberCommonModalType) => {
  const { value: isDesktop } = useMediaQuery('(min-width: 768px)')
  const showToast = useCustomToast()

  const loggedInMobileNumber = loadState('ctxVal') || null
  const { handleGetCartItems, handleAddOrRemoveProductInCart } = CART_HELPER()
  const { handleCheckValidationForProduct } = PACKAGE_HELPER()
  const { handleGetUserDetails, updatePrimaryPatient } = PATIENT_HELPER()

  const loginState = useSelector((state) => state.login)
  const cartState = useSelector((state) => state.cart)
  const userState = useSelector((state) => state.user)
  const packageState = useSelector((state) => state.package)
  const patientState: any = useSelector((state) => state.patient)
  const orderState = useSelector((state) => state.order)

  const loginFlowPrimarySelection = patientState?.loginFlowPrimarySelection
  const isPrimaryModal = loginFlowPrimarySelection || isItPrimaryModal || false

  const [selectedUser, setSelectedUser] = useState<any>({})
  const [loading, setLoading] = useState(false)
  const FAMILY_MEMBERS = isPrimaryModal
    ? userState?.membersList?.filter(
        (item: any) => item?.phoneNumber === loggedInMobileNumber
      )
    : userState?.membersList || []

  useEffect(() => {
    if (isPrimaryModal && !isEmpty(FAMILY_MEMBERS)) {
      const primaryProfiles = getAllPrimaryProfiles(FAMILY_MEMBERS)
      if (primaryProfiles?.length === 1) setSelectedUser(primaryProfiles?.[0])
    }
  }, [open])

  const handleAddProduct = async () => {
    if (Object?.keys(selectedUser)?.length === 0) {
      showToast('danger', 'Please select the patient first')
      return
    }
    if (!loginState?.isUserLoggedIn) {
      dispatch(updateLoginData({ ...loginState, loginModalIsOpen: true }))
      return
    }

    const validationResponse = await handleCheckValidationForProduct(
      patientState?.selectedTest,
      selectedUser
    )
    if (!!validationResponse?.errorMessage) {
      showToast('danger', validationResponse.errorMessage)
      return
    }
    setLoading(true)
    const apiResponse = await handleAddOrRemoveProductInCart(
      INSERT_ACTION,
      selectedUser,
      patientState?.selectedTest
    )
    if (apiResponse?.status === true) {
      showToast('success', 'Test successfully added ')
      if (!!apiResponse?.data[0]?.sameProductAdded) {
        showToast('warning', apiResponse?.data[0]?.message)
      }
      if (!!apiResponse?.data[0]?.cityChanged) {
        showToast('warning', apiResponse?.data[0]?.message)
      }
      const res = await handleGetCartItems()
      dispatch(updateCartData({ ...cartState, ...res?.data }))
      dispatch(
        updateOrderData({
          ...orderState,
          cartDtoList: [...res?.data?.cartDtoList],
          savingDto: initSavingDto,
        })
      )
      dispatch(
        updatePackageData({
          ...packageState,
          productAddingForPatientFlag: true,
        })
      )
      onOpenChange(false)
    }
    setLoading(false)
  }

  const updatePrimaryProfile = async () => {
    if (isEmpty(selectedUser)) {
      showToast('danger', 'Please select the patient first')
      return
    }
    setLoading(true)
    const formData = {
      loggedInUserPhoneNumber: loggedInMobileNumber,
      primaryMemberPatientCode: selectedUser?.code,
      familyMemberList: [],
    }

    const apiResponse = await updatePrimaryPatient(formData)
    showToast(apiResponse?.status ? 'success' : 'warning', apiResponse?.message)
    if (apiResponse?.status === true) {
      // await handleGetUserDetails()
      onOpenChange(false)
    }
    setLoading(false)
  }

  return (
    <CustomDrawerModal
      open={open}
      title={isPrimaryModal ? 'Select Primary Member' : 'Select a member'}
      dialogClassName="p-0"
      onOpenChange={onOpenChange}
      outsideClickDisabled={loginFlowPrimarySelection}
      dialogContentClassName={
        loginFlowPrimarySelection
          ? isDesktop
            ? `[&>button]:hidden`
            : `[&>div>button]:hidden`
          : ''
      }
    >
      <div className="p-6 pt-4 pb-0">
        <p className="text-label-sm text-gray-700 text-center mb-4">
          {isPrimaryModal
            ? 'You can always change Primary Member in Manage Family section'
            : 'Tests will be added for the selected family member'}
        </p>
        <div className="radio">
          <RadioGroup
            onValueChange={(data) => setSelectedUser(data)}
            className="rounded-lg max-h-[256px] overflow-y-auto bg-gray-50 divide-y divide-gray-200 border border-solid border-gray-200"
            value={selectedUser}
          >
            {!!FAMILY_MEMBERS &&
              FAMILY_MEMBERS?.length > 0 &&
              FAMILY_MEMBERS?.map((user: any) => {
                return (
                  <Label
                    key={user?.code}
                    htmlFor={user.code}
                    className="cursor-pointer"
                  >
                    <div className="flex items-start justify-between space-x-2 p-4">
                      <div>
                        <p className="text-label-lg font-semibold mb-1 cursor-pointer">
                          {user.firstName} {user?.lastName}{' '}
                          {user?.relation && (
                            <span className="capitalize">
                              ({String(user?.relation).toLowerCase()})
                            </span>
                          )}
                        </p>
                        <p className="text-body-sm text-gray-700 font-medium capitalize">
                          {user?.age ? user?.age : calculateAge(user?.dob)} Y{' '}
                          <span className="text-gray-400 text-body-md">|</span>{' '}
                          {String(user?.gender)?.toUpperCase()?.substring(0, 1)}{' '}
                          <span className="text-gray-400 text-body-md">|</span>{' '}
                          {user?.phoneNumber}{' '}
                          <span className="text-gray-400 text-body-md">|</span>{' '}
                          {user?.numberOfAccessions} Reports
                        </p>
                      </div>
                      <RadioGroupItem
                        tabIndex={1}
                        value={user}
                        id={user.code}
                      />
                    </div>
                  </Label>
                )
              })}
          </RadioGroup>

          {!isPrimaryModal && (
            <ActionButton variant="secondary"
              title="Add new member"
              icon={<PlusCircle size={18} className="text-primary-500" />}
              iconPosition="start"
              className="rounded-full flex mx-auto mt-3 mb-2"
              onClick={() => {
                dispatch(
                  askUserToAddPatient({
                    ...patientState,
                    addNewPatientModalIsOpen: true,
                  })
                )
              }}
            />
          )}
        </div>
      </div>

      <hr className="border-gray-100" />
      <DialogFooter className="pt-4 p-6 ">
        {isPrimaryModal ? (
          <ActionButton variant="primary"
            title="Update"
            loading={loading}
            loadingText="Adding"
            disabled={loading}
            className="text-label-lg w-full"
            onClick={() => updatePrimaryProfile()}
          />
        ) : (
          <ActionButton variant="primary"
            title="Proceed"
            loading={loading}
            loadingText="Adding"
            disabled={loading}
            className="text-label-lg w-full"
            onClick={handleAddProduct}
          />
        )}
      </DialogFooter>
    </CustomDrawerModal>
  )
}

export default SelectMemberModal
